import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import * as XLSX from 'xlsx';
import { BsCloudUpload, BsDownload, BsXLg } from 'react-icons/bs';
import { Button, buttonVariants } from '@components/Button';
import { validateCsvRows, parseFileToRows } from '@/utils/employeeImport';
import styles from './CsvImportDropzone.module.scss';

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1);

/**
 * Drag-and-drop CSV/Excel importer: parses the file client-side, shows a
 * per-row validation preview (so a bad row is visible before anything is
 * submitted), then hands the caller both the parsed rows AND the original
 * File on confirm — callers that need to upload the raw file (rather than
 * the parsed JSON) can still do so, this component only adds a preview.
 *
 * `previewColumns` is `[{ key, label }]` — drives both the preview table
 * and the default CSV template download, so it's the single source of
 * truth for which fields this instance cares about (e.g. omit `team`
 * where employees are added to one team, not one per row).
 *
 * Pass `onDownloadTemplate` to replace the built-in plain-CSV template with
 * a custom one (e.g. a richer .xlsx with an Instructions sheet).
 *
 * By default the confirm button is disabled when every row failed
 * validation (nothing useful to import). Set `allowConfirmWithErrors` when
 * the caller submits the raw File rather than the parsed rows — the
 * preview is then purely informational and shouldn't block upload of a
 * file whose columns this component's parser doesn't recognize.
 *
 * `parseFile` defaults to the employee-shaped `parseFileToRows` — pass a
 * different `(file) => Promise<row[]>` for other row shapes (e.g. holidays),
 * paired with a matching `validateRows`.
 */
export const CsvImportDropzone = ({
  onImport,
  previewColumns,
  templateFilename = 'employees_template.csv',
  templateRows = [],
  validateRows = validateCsvRows,
  parseFile = parseFileToRows,
  onDownloadTemplate,
  allowConfirmWithErrors = false,
  itemNounSingular = 'employee',
  itemNounPlural = 'employees',
}) => {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const downloadTemplate = () => {
    if (onDownloadTemplate) {
      onDownloadTemplate();
      return;
    }
    const headerRow = previewColumns.map((col) => col.label);
    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...templateRows]);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = templateFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const processFile = async (selectedFile) => {
    const validExt = /\.(csv|xlsx|xls)$/i.test(selectedFile.name);
    if (!validExt) {
      setParseError(
        'Invalid file type. Please upload a .csv, .xlsx or .xls file.'
      );
      return;
    }
    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      setParseError('File exceeds 20 MB. Please split it or use manual entry.');
      return;
    }
    setParseError(null);
    try {
      const parsed = await parseFile(selectedFile);
      if (!parsed.length) {
        setParseError(
          `No ${itemNounSingular} rows found. Make sure the file has data below the header row.`
        );
        return;
      }
      setFile(selectedFile);
      setRows(validateRows(parsed));
    } catch (err) {
      setParseError(err.message);
    }
  };

  const handleFileInput = (e) => {
    const selected = e.target.files[0];
    if (selected) processFile(selected);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) processFile(dropped);
  };

  // Awaits onImport (a no-op await if the caller's onImport is synchronous,
  // so existing callers are unaffected) so the confirm button can show a
  // loading state instead of instantly resetting to the idle dropzone while
  // an async submit (e.g. a Firestore write) is still in flight. Only clears
  // the preview on success — if the caller signals failure by resolving
  // `false`, the file/rows stay so the user can retry without re-uploading.
  const handleConfirm = async () => {
    const validRows = rows
      .filter((row) => row._errors.length === 0)
      .map(({ _errors, _fieldErrors, ...emp }) => emp);
    setIsSubmitting(true);
    try {
      const outcome = await onImport({ file, validRows, allRows: rows });
      if (outcome !== false) {
        setRows(null);
        setFile(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setRows(null);
    setFile(null);
    setParseError(null);
  };

  // ── Preview state ──────────────────────────────────────────

  if (rows !== null) {
    const validCount = rows.filter((row) => row._errors.length === 0).length;
    const errorCount = rows.length - validCount;

    return (
      <div className={styles.csvSection}>
        <div className={styles.csvPreviewHeader}>
          <div className={styles.csvPreviewMeta}>
            <span className={styles.csvPreviewCount}>
              {rows.length}{' '}
              {rows.length === 1 ? itemNounSingular : itemNounPlural} found
            </span>
            {errorCount > 0 && (
              <span className={styles.csvPreviewErrorBadge}>
                {errorCount} with errors
              </span>
            )}
          </div>
          <Button
            variant={buttonVariants.outline}
            text="Clear & re-upload"
            icon={<BsXLg size={10} />}
            onClick={handleClear}
            disabled={isSubmitting}
          />
        </div>

        <div className={styles.csvTableWrapper}>
          <table className={styles.csvTable}>
            <thead>
              <tr>
                <th>#</th>
                {previewColumns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={i}
                  className={
                    row._errors.length ? styles.csvRowError : styles.csvRowValid
                  }
                  title={
                    row._errors.length ? row._errors.join(', ') : undefined
                  }
                >
                  <td className={styles.csvCellNum}>{i + 1}</td>
                  {previewColumns.map((col) => (
                    <td key={col.key}>
                      {row[col.key] || (
                        <span className={styles.csvCellEmpty}>—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.csvPreviewFooter}>
          {errorCount > 0 && (
            <p className={styles.csvPreviewFooterNote}>
              {errorCount} row{errorCount > 1 ? 's' : ''} with errors will be
              skipped.
            </p>
          )}
          <div className={styles.csvPreviewActions}>
            <Button
              variant={buttonVariants.outline}
              text="Cancel"
              onClick={handleClear}
              disabled={isSubmitting}
            />
            <Button
              variant={buttonVariants.primary}
              text={
                validCount > 0
                  ? `Add ${validCount} ${capitalize(validCount === 1 ? itemNounSingular : itemNounPlural)}`
                  : 'Use this file'
              }
              onClick={handleConfirm}
              loading={isSubmitting}
              disabled={
                allowConfirmWithErrors ? rows.length === 0 : validCount === 0
              }
            />
          </div>
        </div>
      </div>
    );
  }

  // ── Idle state ─────────────────────────────────────────────

  return (
    <div className={styles.csvSection}>
      <div
        className={classNames(styles.csvDropZone, {
          [styles.csvDropZoneActive]: isDragOver,
          [styles.csvDropZoneErrorState]: !!parseError,
        })}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) =>
          (e.key === 'Enter' || e.key === ' ') && fileInputRef.current?.click()
        }
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleFileInput}
        />
        <div className={styles.csvDropZoneIcon}>
          <BsCloudUpload
            size={26}
            color={parseError ? 'var(--error-500)' : 'var(--blue-600)'}
          />
        </div>
        <p className={styles.csvDropZoneTitle}>
          Drop your file here, or{' '}
          <span className={styles.csvDropZoneBrowse}>browse</span>
        </p>
        <p className={styles.csvDropZoneSub}>
          Supports CSV, XLSX &mdash; max 20 MB
        </p>
      </div>

      <div className={styles.csvTemplateLine}>
        <Button
          variant={buttonVariants.ghost}
          text="Download template CSV"
          icon={<BsDownload size={12} />}
          onClick={downloadTemplate}
        />
      </div>

      {parseError && <div className={styles.csvParseError}>{parseError}</div>}
    </div>
  );
};
